// frontend/src/components/common/DateRangeFilter.jsx
import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { BUTTON_RADIUS, FIELD_HEIGHT_SX, FS, SP } from '../../theme/tokens';
import { berlinDateISO, formatDate } from '../../utils/format';

/**
 * DateRangeFilter — a date range as ONE criterion inside a filter sheet/popover.
 *
 * @description
 *   Presets first (Today · Last 7 days · Last 30 days · This month · Custom),
 *   then two plain text fields that take and show `dd.MM.yyyy` — the ONE date
 *   format of the house (`utils/format.js`, concept N3.9). It exists because
 *   `<TextField type="date">` renders the BROWSER's format: on an en-US machine
 *   the same filter reads `mm/dd/yyyy`, and the table beside it reads
 *   `dd.MM.yyyy` (concept FT5, case C6).
 *
 *   The value is `{ from, to }` in `YYYY-MM-DD` (the house spelling,
 *   `berlinDateISO`), never a `Date` — the range is a pair of calendar days,
 *   not two instants.
 *
 * @useWhen
 *   - A date-range criterion inside `FilterSheet` (`criteria` entry with
 *     `kind: 'date'`)
 *   - Any filter surface that must show `dd.MM.yyyy` while the browser's own
 *     date field would show the machine's format
 *
 * @dontUseFor
 *   - A single date (that is a plain field, not a range)
 *   - A period for KPI figures — that is `RangeToggle` (a frame segment); a
 *     date filter HIDES records, a frame segment does not (concept N3.5)
 *   - Free-text date search — dates in a filter are `dd.MM.yyyy` or a facet
 *
 * @related
 *   - `FilterSheet` — renders this for `criteria.kind === 'date'`
 *   - `RangeToggle` — the other time control, but for figures, not records
 *
 * @reuseHints
 *   - Invoice list (issued date), RMA list (created date), stock movements
 *   - Every place that migrates away from `<input type="date">`
 *   - 🔴 Forward windows for ETAs and delivery dates: `next7` / `next14` /
 *     `next30`, past window `overdue` — pass them as `presets`, and judge a
 *     record's day with `tagImBereich(berlinDateISO(iso), vorwahlBereich(key, heute))`
 *     (the ONE day predicate; filter and KPI must share it, G8a/Plan-W5)
 *
 * @props
 *   label    : string   — section heading (default "Date"); it also NAMES the
 *                         preset group (`role="group"` + `aria-label="<label> range"`)
 *   value    : object   — `{ from, to }` as `YYYY-MM-DD`; either may be null
 *   onChange : function — called with the next `{ from, to }`. 🔴 A range that
 *                         cannot exist is NOT reported: `from > to` is a field
 *                         error on "To" ("End date is before start date") and
 *                         fires NO `onChange` — the caller must never filter on
 *                         a span that does not exist.
 *   presets  : array    — `[{ key, label }]`; `key` from `vorwahlBereich`.
 *                         Default: `DATE_RANGE_PRESETS` (the five house presets).
 *                         🔴 The "custom" fallback is tied to THIS list: a
 *                         caller who supplies no `custom` entry gets no pressed
 *                         button for a hand-typed range — none of their buttons
 *                         means "not a preset" (R15 W3).
 *   disabled : boolean  — disables every control
 */

/** The house date spelling inside a filter — `dd.MM.yyyy` (concept N3.9). */
const DE_DATUM = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;

/** The five house presets (concept §3.2). `custom` carries no range of its own. */
export const DATE_RANGE_PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'last7', label: 'Last 7 days' },
  { key: 'last30', label: 'Last 30 days' },
  { key: 'thisMonth', label: 'This month' },
  { key: 'custom', label: 'Custom' },
];

/**
 * `dd.MM.yyyy` → `YYYY-MM-DD`, or `null` when the text is not a real calendar
 * day. The round-trip through `Date.UTC` is the check: `31.02.2026` parses as
 * three numbers but rolls over to 03.03., so the field would accept a day that
 * does not exist.
 *
 * @param {string} text
 * @returns {string|null}
 */
export function parseDeDatum(text) {
  const treffer = DE_DATUM.exec(String(text ?? '').trim());
  if (!treffer) return null;
  const tag = Number(treffer[1]);
  const monat = Number(treffer[2]);
  const jahr = Number(treffer[3]);
  const geprueft = new Date(Date.UTC(jahr, monat - 1, tag));
  if (
    geprueft.getUTCFullYear() !== jahr
    || geprueft.getUTCMonth() !== monat - 1
    || geprueft.getUTCDate() !== tag
  ) {
    return null;
  }
  return `${jahr}-${String(monat).padStart(2, '0')}-${String(tag).padStart(2, '0')}`;
}

/** `YYYY-MM-DD` → `dd.MM.yyyy`; `''` for an empty value. */
export function formatDeDatum(iso) {
  return iso ? formatDate(iso) : '';
}

/** `YYYY-MM-DD` minus N days — UTC arithmetic, no local-midnight drift. */
function tageVorher(iso, tage) {
  const [jahr, monat, tag] = iso.split('-').map(Number);
  return new Date(Date.UTC(jahr, monat - 1, tag - tage)).toISOString().slice(0, 10);
}

/** `YYYY-MM-DD` plus N days — dieselbe UTC-Arithmetik wie `tageVorher`. */
function tageNach(iso, tage) {
  const [jahr, monat, tag] = iso.split('-').map(Number);
  return new Date(Date.UTC(jahr, monat - 1, tag + tage)).toISOString().slice(0, 10);
}

/**
 * The range a preset stands for, relative to `heute` (`YYYY-MM-DD`).
 * `custom` has no range — it is the state "the fields, not a preset".
 *
 * 🔴 Die vier VORWÄRTS-Schlüssel (UI2 Teil 10, G8a §2.4) kamen am 2026-10-20
 * dazu. Die fünf Hausvorwahlen fragen die VERGANGENHEIT („Last 7 days"); eine
 * ETA-Frage ist vorwärts („kommt in den nächsten 7 Tagen"). Sie sind rein
 * additiv: die bestehenden Schlüssel und `DATE_RANGE_PRESETS` sind unverändert.
 *
 * 🔴 **Die Fenster sind N Kalendertage EINSCHLIESSLICH heute** — `next7` ist
 * heute .. heute + 6, nicht + 7. Die Zählweise ist die der Hausvorwahl `last7`
 * (`tageVorher(heute, 6)` = sieben Tage); `heute + N` wäre acht und hiesse
 * „Next 7 days" falsch (Plan-W5). `overdue` ist einseitig: alles VOR dem
 * heutigen Tag (`to` = gestern, `from` = null).
 *
 * @param {string} key
 * @param {string} heute
 * @returns {{from: string|null, to: string|null}|null}
 */
export function vorwahlBereich(key, heute) {
  switch (key) {
    case 'today':
      return { from: heute, to: heute };
    case 'last7':
      return { from: tageVorher(heute, 6), to: heute };
    case 'last30':
      return { from: tageVorher(heute, 29), to: heute };
    case 'thisMonth':
      return { from: `${heute.slice(0, 7)}-01`, to: heute };
    case 'next7':
      return { from: heute, to: tageNach(heute, 6) };
    case 'next14':
      return { from: heute, to: tageNach(heute, 13) };
    case 'next30':
      return { from: heute, to: tageNach(heute, 29) };
    case 'overdue':
      return { from: null, to: tageVorher(heute, 1) };
    default:
      return null;
  }
}

/**
 * Liegt ein Kalendertag (`YYYY-MM-DD`) im Bereich? Je gesetzte Grenze wird
 * verglichen, eine fehlende Grenze ist offen.
 *
 * 🔴 **Tag gegen Tag als ZEICHENKETTE**, nicht `Date` gegen Zeichenkette:
 * `'2026-10-20' <= Date` ergibt `NaN` ⇒ `false`, und der Filter fiele still
 * leer aus. ISO-Tage sind lexikographisch geordnet, der Vergleich ist damit
 * exakt und ohne Zeitzone.
 *
 * 🔴 Diese Funktion ist die EINE Stelle, an der Filter und KPI dieselbe Menge
 * rechnen (Plan-W5): „Next 7 days" und die Kachel „Arriving this week" rufen
 * beide `tagImBereich(berlinDateISO(eta), vorwahlBereich('next7', heute))` —
 * zwei Stellen mit zwei Tagesbegriffen wären zwei verschiedene Mengen unter
 * derselben Wendung.
 *
 * @param {string|null} tag `YYYY-MM-DD`
 * @param {{from: string|null, to: string|null}|null} bereich
 * @returns {boolean}
 */
export function tagImBereich(tag, bereich) {
  if (!tag || !bereich) return false;
  if (bereich.from && tag < bereich.from) return false;
  if (bereich.to && tag > bereich.to) return false;
  return true;
}

export default function DateRangeFilter({
  label = 'Date',
  value,
  onChange,
  presets = DATE_RANGE_PRESETS,
  disabled = false,
}) {
  const heute = berlinDateISO(new Date());
  const vonFeldRef = useRef(null);
  const [vonText, setVonText] = useState(() => formatDeDatum(value?.from));
  const [bisText, setBisText] = useState(() => formatDeDatum(value?.to));
  const [vonBeruehrt, setVonBeruehrt] = useState(false);
  const [bisBeruehrt, setBisBeruehrt] = useState(false);

  // Von aussen gesetzte Werte (Vorwahl, „Clear all") schreiben in die Felder.
  // 🔴 Nur, wenn der Text NICHT schon dasselbe Datum meint: sonst normalisiert
  // der Effekt mitten im Tippen (`1.1.2026` → `01.01.2026`) und reisst den
  // Schreibzeiger an das Ende.
  useEffect(() => {
    setVonText((alt) => (parseDeDatum(alt) === (value?.from ?? null) ? alt : formatDeDatum(value?.from)));
  }, [value?.from]);
  useEffect(() => {
    setBisText((alt) => (parseDeDatum(alt) === (value?.to ?? null) ? alt : formatDeDatum(value?.to)));
  }, [value?.to]);

  // Der SICHTBARE Text ist die Wahrheit fuer die Querfeldpruefung — nicht die
  // Prop: eine Eingabe, die nie als `onChange` hinausging (unlesbar oder
  // verdreht), steht trotzdem im Feld und muss dort beurteilt werden.
  const vonIso = parseDeDatum(vonText);
  const bisIso = parseDeDatum(bisText);
  // 🔴 Was die GEGENSEITE wert ist, wenn dieses Feld geaendert wird: der
  // sichtbare Text, wenn er lesbar ist; leer, wenn er leer ist; und sonst der
  // PROP-Wert. Der dritte Fall ist kein Schoenheitsfehler — ein halb getipptes
  // Gegenfeld darf eine gueltige Grenze nicht stillschweigend wegwerfen (das
  // tat die erste Fassung dieses Schnitts).
  const vonWert = vonIso ?? (vonText.trim() === '' ? null : (value?.from ?? null));
  const bisWert = bisIso ?? (bisText.trim() === '' ? null : (value?.to ?? null));

  const vonFehler = vonBeruehrt && vonText.trim() !== '' && !vonIso;
  // 🔴 Der Querfeld-Check (R15 W7 / R16 W6): „von > bis" ist kein Bereich. Der
  // Fehler steht am BIS-Feld — dort steht die Grenze, die zu weit vorn liegt.
  const bereichFehler = Boolean(vonIso && bisIso && vonIso > bisIso);
  const bisFehler = (bisBeruehrt && bisText.trim() !== '' && !bisIso) || bereichFehler;

  const hatWert = Boolean(value?.from) || Boolean(value?.to);
  const aktive = presets.find((p) => {
    const bereich = vorwahlBereich(p.key, heute);
    return bereich && bereich.from === value?.from && bereich.to === value?.to;
  });
  // 🔴 Der „Custom"-Rueckfall haengt an DIESER `presets`-Liste (R15 W3), nicht
  // an einem Literal: ein Aufrufer, dessen Liste keinen `custom`-Eintrag hat,
  // bekommt fuer einen selbst getippten Bereich KEINEN gedrueckten Knopf — und
  // das ist die ehrliche Lage, denn keiner SEINER Knoepfe bedeutet „kein
  // Vorgabewert". Der Defekt war der andere Fall: ein Knopf blieb ungedrueckt,
  // OBWOHL die Liste einen `custom`-Eintrag hatte.
  const hatCustom = presets.some((p) => p.key === 'custom');
  const aktiverKey = aktive ? aktive.key : (hatWert && hatCustom ? 'custom' : null);

  const melde = (from, to) => onChange?.({ from: from ?? null, to: to ?? null });

  const waehleVorwahl = (key) => {
    const bereich = vorwahlBereich(key, heute);
    if (!bereich) {
      // 🔴 „Custom" setzt KEINEN Bereich und meldet NICHTS: die gedrueckte
      // Vorwahl BLEIBT stehen (der Wert aendert sich nicht, also bleibt auch
      // `aria-pressed`), die Felder bleiben stehen, und der Fokus geht dorthin,
      // wo man weiterschreibt. Hier stand bis zum 2026-10-12 „es nimmt die
      // Vorwahl weg" — der Zweig ruft kein `melde`, das war falsch (R15 W4).
      setVonBeruehrt(false);
      setBisBeruehrt(false);
      vonFeldRef.current?.focus();
      return;
    }
    setVonText(formatDeDatum(bereich.from));
    setBisText(formatDeDatum(bereich.to));
    setVonBeruehrt(false);
    setBisBeruehrt(false);
    melde(bereich.from, bereich.to);
  };

  const aendereVon = (roh) => {
    setVonText(roh);
    if (roh.trim() === '') {
      melde(null, bisWert);
      return;
    }
    const iso = parseDeDatum(roh);
    if (!iso) return;
    // „von > bis": die Eingabe bleibt stehen, gemeldet wird sie am Bis-Feld,
    // und der Aufrufer hoert NICHTS — er darf nicht auf eine Spanne filtern,
    // die es nicht gibt.
    if (bisWert && iso > bisWert) return;
    melde(iso, bisWert);
  };

  const aendereBis = (roh) => {
    setBisText(roh);
    if (roh.trim() === '') {
      melde(vonWert, null);
      return;
    }
    const iso = parseDeDatum(roh);
    if (!iso) return;
    if (vonWert && iso < vonWert) return;
    melde(vonWert, iso);
  };

  return (
    <Box>
      {/* 🔴 Die Abschnittsueberschrift benennt die Vorwahlgruppe (R15 W8 /
          R16 W2): ohne einen Namen zeichnet MUI eine namenlose `role="group"`,
          und die fuenf Knoepfe stehen ohne Bezug zu „Date" da.
          🔴 R16 W1: die GRUPPE heisst „<Abschnitt> range", nicht wie der
          Abschnitt selbst. Der sichtbare Titel bleibt „ETA"; der Gruppenname
          sagt, was die Knoepfe TUN (einen Bereich waehlen), und deckt sich
          nicht mehr Wort fuer Wort mit dem Datumsfeld in `CalendarView` (das
          dort in seinem eigenen Blatt korrekt „ETA" heisst und hier NICHT
          angefasst wird). */}
      <Typography sx={{ fontSize: FS.sm, color: 'text.secondary', mb: SP.chip }}>
        {label}
      </Typography>

      <Stack
        direction="row"
        role="group"
        aria-label={`${label} range`}
        sx={{ flexWrap: 'wrap', gap: SP.chip, mb: SP.sm }}
      >
        {presets.map((p) => (
          <Button
            key={p.key}
            size="small"
            disabled={disabled}
            variant={p.key === aktiverKey ? 'contained' : 'outlined'}
            aria-pressed={p.key === aktiverKey}
            onClick={() => waehleVorwahl(p.key)}
            sx={{ borderRadius: `${BUTTON_RADIUS}px`, textTransform: 'none', ...FIELD_HEIGHT_SX }}
          >
            {p.label}
          </Button>
        ))}
      </Stack>

      <Stack direction="row" sx={{ gap: SP.tight }}>
        <TextField
          size="small"
          fullWidth
          disabled={disabled}
          inputRef={vonFeldRef}
          label="From"
          placeholder="dd.MM.yyyy"
          value={vonText}
          error={vonFehler}
          helperText={vonFehler ? 'Use dd.MM.yyyy' : ' '}
          onChange={(e) => aendereVon(e.target.value)}
          onBlur={() => setVonBeruehrt(true)}
          inputProps={{ inputMode: 'numeric', 'aria-label': `${label} from` }}
        />
        <TextField
          size="small"
          fullWidth
          disabled={disabled}
          label="To"
          placeholder="dd.MM.yyyy"
          value={bisText}
          error={bisFehler}
          helperText={bisFehler
            ? (bereichFehler ? 'End date is before start date' : 'Use dd.MM.yyyy')
            : ' '}
          onChange={(e) => aendereBis(e.target.value)}
          onBlur={() => setBisBeruehrt(true)}
          inputProps={{ inputMode: 'numeric', 'aria-label': `${label} to` }}
        />
      </Stack>
    </Box>
  );
}
